import type { WalletMeta } from './types';

const MODAL_ID = '__wc_modal__';
const MASCOT_URL = 'https://dailytrip.1dollar.day/assets/character-mascot.png';

/** Default accent (OneWallet green). Per-wallet accent comes from WalletMeta.accent. */
const DEFAULT_ACCENT = '#2bea6a';

function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const int = parseInt(full, 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}

function rgba(hex: string, alpha: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Lighten (amount > 0) or darken (amount < 0) a hex color by mixing toward white/black. */
function shadeColor(hex: string, amount: number): string {
  const { r, g, b } = hexToRgb(hex);
  const target = amount < 0 ? 0 : 255;
  const p = Math.abs(amount);
  const mix = (c: number) => Math.round((target - c) * p + c);
  const toHex = (c: number) =>
    Math.max(0, Math.min(255, c)).toString(16).padStart(2, '0');
  return `#${toHex(mix(r))}${toHex(mix(g))}${toHex(mix(b))}`;
}

function getStyles(
  theme: 'light' | 'dark',
  accent: string = DEFAULT_ACCENT,
  mono = false,
): string {
  // Monochrome wallets (e.g. TonPass) are always a flat white sheet with a black/white
  // palette — dark theme and gradients are ignored so the surface stays pure white.
  const isDark = !mono && theme === 'dark';
  const bg = mono ? '#ffffff' : isDark ? '#1a1f2e' : '#ffffff';
  const bgCard = mono ? '#f4f4f5' : isDark ? '#222834' : '#f0f7ff';
  const bgGlass = mono
    ? 'rgba(255,255,255,0.95)'
    : isDark
      ? 'rgba(34,40,52,0.9)'
      : 'rgba(255,255,255,0.9)';
  const text = mono ? '#0a0a0a' : isDark ? '#f0f4f8' : '#1a1f2e';
  const textSecondary = mono ? '#6b7280' : isDark ? '#6b7a8d' : '#6b7c8d';
  const border = mono ? '#e5e7eb' : isDark ? '#2d3545' : '#e0eaf4';
  // Preserve the original green palette byte-for-byte for the default accent so existing
  // wallets (OneDollar) render exactly as before; only derive shades for a custom accent.
  const isDefault = accent === DEFAULT_ACCENT;
  const gradientAccent = mono
    ? accent // flat black, no gradient
    : isDefault
      ? 'linear-gradient(135deg, #1db954, #2bea6a, #4af58a)'
      : `linear-gradient(135deg, ${shadeColor(accent, -0.28)}, ${accent}, ${shadeColor(accent, 0.28)})`;
  const gradientBg = mono
    ? '#ffffff' // flat white, always
    : isDark
      ? 'linear-gradient(180deg, #1a1f2e 0%, #222834 100%)'
      : isDefault
        ? 'linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 50%, #f0f7ff 100%)'
        : `linear-gradient(135deg, #f0f7ff 0%, ${rgba(accent, 0.1)} 50%, #f0f7ff 100%)`;
  const glowAccent = mono ? rgba('#000000', 0.06) : rgba(accent, 0.15);
  const glowStrong = mono ? rgba('#000000', 0.14) : rgba(accent, 0.3);
  // "Open in Telegram" keeps the Telegram-brand blue even in the mono (black/white) palette.
  const deeplinkBg = mono ? '#35a7fb' : gradientAccent;
  const deeplinkGlow = mono ? rgba('#35a7fb', 0.35) : glowAccent;
  const deeplinkGlowStrong = mono ? rgba('#35a7fb', 0.45) : glowStrong;

  return `
    @keyframes wc-fadeIn { from{opacity:0} to{opacity:1} }
    @keyframes wc-slideUp {
      from{opacity:0;transform:translateY(40px) scale(0.96)}
      to{opacity:1;transform:translateY(0) scale(1)}
    }
    @keyframes wc-float {
      0%,100%{transform:translateY(0)}
      50%{transform:translateY(-6px)}
    }
    @keyframes wc-glow {
      0%,100%{box-shadow:0 0 20px ${glowAccent}}
      50%{box-shadow:0 0 40px ${glowStrong}}
    }
    @keyframes wc-popIn {
      from{transform:scale(0);opacity:0}
      50%{transform:scale(1.15)}
      to{transform:scale(1);opacity:1}
    }
    @keyframes wc-spin { to{transform:rotate(360deg)} }
    @keyframes wc-shimmer {
      0%{background-position:-200% 0}
      100%{background-position:200% 0}
    }

    .wc-overlay {
      position:fixed; inset:0; z-index:99999;
      background:rgba(0,0,0,${isDark ? 0.6 : 0.35});
      display:flex; align-items:center; justify-content:center;
      font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;
      backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px);
      animation:wc-fadeIn .25s ease;
    }
    .wc-modal {
      background:${gradientBg}; width:100%; max-width:400px;
      border-radius:28px; overflow:hidden; color:${text};
      border:1px solid ${border};
      box-shadow:0 24px 80px rgba(0,0,0,${isDark ? 0.5 : 0.12}),
                 0 0 0 1px ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
      animation:wc-slideUp .35s cubic-bezier(.16,1,.3,1);
      max-height:90vh; overflow-y:auto; position:relative;
    }

    /* Decorative glow orb */
    .wc-modal::before {
      content:''; position:absolute; top:-60px; right:-60px;
      width:180px; height:180px; border-radius:50%;
      background:radial-gradient(circle, ${glowAccent} 0%, transparent 70%);
      pointer-events:none; z-index:0;
    }

    /* Header */
    .wc-header {
      display:flex; align-items:center; justify-content:space-between;
      padding:24px 24px 0; position:relative; z-index:1;
    }
    .wc-header-left {
      display:flex; align-items:center; gap:10px;
    }
    .wc-header-icon {
      width:36px; height:36px; border-radius:12px; object-fit:cover;
      border:2px solid ${border}; box-shadow:0 2px 8px rgba(0,0,0,0.1);
    }
    .wc-title {
      font-size:18px; font-weight:800; margin:0;
      background:${gradientAccent}; -webkit-background-clip:text;
      -webkit-text-fill-color:transparent; background-clip:text;
    }
    .wc-close {
      width:32px; height:32px; display:flex; align-items:center; justify-content:center;
      background:${bgCard}; border:1px solid ${border}; border-radius:50%;
      cursor:pointer; font-size:14px; color:${textSecondary};
      transition:all .2s;
    }
    .wc-close:hover { background:${border}; color:${text}; transform:rotate(90deg); }

    .wc-subtitle {
      font-size:13px; color:${textSecondary}; margin:12px 24px 0;
      line-height:1.5; position:relative; z-index:1;
    }

    .wc-body { padding:20px 24px 24px; position:relative; z-index:1; }

    /* Mascot */
    .wc-mascot {
      display:flex; justify-content:center; margin:8px 0 4px;
    }
    .wc-mascot img {
      width:100px; height:100px; object-fit:contain;
      animation:wc-float 3s ease-in-out infinite;
      filter:drop-shadow(0 8px 24px ${rgba(accent, 0.2)});
    }

    /* Wallet list */
    .wc-wallet-list { list-style:none; margin:0; padding:0; }
    .wc-wallet-item {
      display:flex; align-items:center; gap:14px;
      padding:14px 16px; border-radius:16px; cursor:pointer;
      background:${bgGlass}; border:1.5px solid ${border};
      margin-bottom:10px; transition:all .2s; position:relative;
      overflow:hidden;
    }
    .wc-wallet-item::before {
      content:''; position:absolute; inset:0;
      background:${gradientAccent}; opacity:0;
      transition:opacity .2s; border-radius:16px;
    }
    .wc-wallet-item:hover::before { opacity:0.06; }
    .wc-wallet-item:hover {
      border-color:${accent}; transform:translateY(-2px);
      box-shadow:0 8px 24px ${glowAccent};
    }
    .wc-wallet-list-icon {
      width:44px; height:44px; border-radius:14px; object-fit:cover;
      position:relative; z-index:1;
      border:2px solid ${border}; box-shadow:0 2px 8px rgba(0,0,0,0.08);
    }
    .wc-wallet-list-icon-placeholder {
      width:44px; height:44px; border-radius:14px;
      background:${gradientAccent};
      display:flex; align-items:center; justify-content:center;
      color:#fff; font-weight:800; font-size:18px;
      position:relative; z-index:1;
    }
    .wc-wallet-item-info { flex:1; position:relative; z-index:1; }
    .wc-wallet-item-name { font-size:15px; font-weight:700; }
    .wc-wallet-item-desc { font-size:12px; color:${textSecondary}; margin-top:2px; }
    .wc-wallet-item-arrow {
      color:${accent}; font-size:18px; font-weight:700;
      position:relative; z-index:1; transition:transform .2s;
    }
    .wc-wallet-item:hover .wc-wallet-item-arrow { transform:translateX(4px); }

    /* Grid picker */
    .wc-wallet-grid {
      display:grid; grid-template-columns:repeat(3,1fr);
      gap:12px; list-style:none; margin:0; padding:0;
    }
    .wc-wallet-grid-item {
      display:flex; flex-direction:column; align-items:center;
      gap:10px; cursor:pointer; padding:16px 8px; border-radius:20px;
      background:${bgGlass}; border:1.5px solid ${border};
      transition:all .2s;
    }
    .wc-wallet-grid-item:hover {
      border-color:${accent}; transform:translateY(-3px);
      box-shadow:0 8px 24px ${glowAccent};
    }
    .wc-wallet-grid-icon {
      width:52px; height:52px; border-radius:16px;
      object-fit:cover; border:2px solid ${border};
      box-shadow:0 4px 12px rgba(0,0,0,0.08);
    }
    .wc-wallet-grid-icon-placeholder {
      width:52px; height:52px; border-radius:16px;
      background:${gradientAccent};
      display:flex; align-items:center; justify-content:center;
      color:#fff; font-weight:800; font-size:20px;
    }
    .wc-wallet-grid-name {
      font-size:12px; font-weight:600; text-align:center;
      color:${text}; line-height:1.3;
    }

    /* Tabs */
    .wc-tabs {
      display:flex; background:${bgCard}; border-radius:14px;
      padding:4px; margin-bottom:20px; border:1px solid ${border};
    }
    .wc-tab {
      flex:1; padding:10px; text-align:center; cursor:pointer;
      font-size:13px; font-weight:700; background:transparent;
      border:none; color:${textSecondary}; border-radius:11px;
      transition:all .25s;
    }
    .wc-tab:hover { color:${text}; }
    .wc-tab.active {
      background:${gradientAccent}; color:#fff;
      box-shadow:0 4px 16px ${glowAccent};
    }

    /* Deeplink */
    .wc-deeplink-btn {
      display:flex; align-items:center; justify-content:center; gap:10px;
      width:100%; padding:16px; border:none; border-radius:16px;
      background:${deeplinkBg}; color:#fff;
      font-size:15px; font-weight:800; cursor:pointer;
      text-decoration:none; transition:all .25s;
      box-shadow:0 4px 24px ${deeplinkGlow};
      box-sizing:border-box; position:relative; overflow:hidden;
    }
    .wc-deeplink-btn::after {
      content:''; position:absolute; inset:0;
      background:linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
      background-size:200% 100%;
      animation:wc-shimmer 2.5s ease-in-out infinite;
    }
    .wc-deeplink-btn:hover {
      transform:translateY(-2px);
      box-shadow:0 8px 32px ${deeplinkGlowStrong};
    }
    .wc-deeplink-btn svg { position:relative; z-index:1; }
    .wc-deeplink-btn span { position:relative; z-index:1; }

    /* QR */
    .wc-qr-container {
      display:flex; flex-direction:column; align-items:center; gap:16px;
    }
    .wc-qr-wrap {
      padding:16px; background:#fff; border-radius:20px;
      box-shadow:0 4px 24px rgba(0,0,0,${isDark ? 0.3 : 0.08});
      border:2px solid ${glowAccent};
    }
    .wc-qr-wrap img { display:block; border-radius:8px; }
    .wc-qr-hint { font-size:12px; color:${textSecondary}; text-align:center; }

    /* OR divider (mono layout: QR + button shown together) */
    .wc-or {
      display:flex; align-items:center; text-align:center;
      color:${textSecondary}; font-size:12px; font-weight:700;
      margin:18px 0;
    }
    .wc-or::before, .wc-or::after {
      content:''; flex:1; height:1px; background:${border};
    }
    .wc-or span { padding:0 14px; }

    /* Status */
    .wc-status {
      display:flex; align-items:center; justify-content:center;
      gap:10px; padding:20px 0 4px; font-size:13px; color:${textSecondary};
    }
    .wc-spinner {
      width:18px; height:18px; border:2.5px solid ${border};
      border-top-color:${accent}; border-radius:50%;
      animation:wc-spin .7s linear infinite;
    }

    /* Footer */
    .wc-footer {
      text-align:center; padding:4px 0 20px;
      font-size:11px; color:${isDark ? '#3d5468' : '#b0bec5'};
      position:relative; z-index:1;
    }

    /* ── Success screen ── */
    .wc-success {
      display:flex; flex-direction:column; align-items:center;
      padding:8px 0 4px; gap:20px;
    }
    .wc-success-check {
      width:72px; height:72px; border-radius:50%;
      background:${gradientAccent};
      display:flex; align-items:center; justify-content:center;
      box-shadow:0 8px 32px ${glowStrong};
      animation:wc-popIn .4s cubic-bezier(.16,1,.3,1), wc-glow 2s ease-in-out infinite;
    }
    .wc-success-check svg { width:36px; height:36px; color:#fff; }
    .wc-success-title {
      font-size:20px; font-weight:800;
      background:${gradientAccent}; -webkit-background-clip:text;
      -webkit-text-fill-color:transparent; background-clip:text;
    }
    .wc-success-user {
      display:flex; align-items:center; gap:14px;
      background:${bgGlass}; border:1.5px solid ${border};
      border-radius:18px; padding:16px 18px; width:100%;
      box-sizing:border-box; backdrop-filter:blur(8px);
    }
    .wc-success-avatar {
      width:50px; height:50px; border-radius:50%; object-fit:cover;
      flex-shrink:0; border:2.5px solid ${accent};
      box-shadow:0 4px 16px ${glowAccent};
    }
    .wc-success-avatar-placeholder {
      width:50px; height:50px; border-radius:50%;
      background:${gradientAccent};
      display:flex; align-items:center; justify-content:center;
      color:#fff; font-weight:800; font-size:18px; flex-shrink:0;
      box-shadow:0 4px 16px ${glowAccent};
    }
    .wc-success-user-info { flex:1; min-width:0; }
    .wc-success-user-name { font-size:16px; font-weight:700; color:${text}; }
    .wc-success-user-addr {
      font-size:11px; color:${textSecondary}; margin-top:4px;
      font-family:'SF Mono',Monaco,Consolas,monospace;
      word-break:break-all; line-height:1.4;
    }
    .wc-success-done {
      width:100%; padding:16px; border:none; border-radius:16px;
      background:${gradientAccent}; color:#fff;
      font-size:15px; font-weight:800; cursor:pointer;
      transition:all .25s; position:relative; overflow:hidden;
      box-shadow:0 4px 24px ${glowAccent};
    }
    .wc-success-done::after {
      content:''; position:absolute; inset:0;
      background:linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent);
      background-size:200% 100%;
      animation:wc-shimmer 2.5s ease-in-out infinite;
    }
    .wc-success-done:hover {
      transform:translateY(-2px);
      box-shadow:0 8px 32px ${glowStrong};
    }
  `;
}

const TELEGRAM_SVG = `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 1 0 24 12.056A12.01 12.01 0 0 0 11.944 0Zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472c-.18 1.898-.962 6.502-1.36 8.627c-.168.9-.499 1.201-.82 1.23c-.696.065-1.225-.46-1.9-.902c-1.056-.693-1.653-1.124-2.678-1.8c-1.185-.78-.417-1.21.258-1.91c.177-.184 3.247-2.977 3.307-3.23c.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345c-.48.33-.913.49-1.302.48c-.428-.008-1.252-.241-1.865-.44c-.752-.245-1.349-.374-1.297-.789c.027-.216.325-.437.893-.663c3.498-1.524 5.83-2.529 6.998-3.014c3.332-1.386 4.025-1.627 4.476-1.635Z"/></svg>`;

function generateQRDataUrl(text: string, size = 220): string {
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&margin=8`;
}

export class Modal {
  private overlay: HTMLDivElement | null = null;
  private theme: 'light' | 'dark';
  private accent: string = DEFAULT_ACCENT;
  private mono = false;
  private onClose: () => void;

  constructor(theme: 'light' | 'dark', onClose: () => void) {
    this.theme = theme;
    this.onClose = onClose;
  }

  /** Lightweight self-dismissing toast (not a modal — doesn't block). Used e.g. to notify
   *  "Session expired" on init without reloading the page. */
  static toast(message: string, theme: 'light' | 'dark' = 'dark'): void {
    if (typeof document === 'undefined') return;
    const dark = theme !== 'light';
    const el = document.createElement('div');
    el.textContent = message;
    el.style.cssText = [
      'position:fixed',
      'top:16px',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:2147483647',
      'max-width:90%',
      'padding:12px 18px',
      'border-radius:12px',
      `background:${dark ? '#1c1c1e' : '#ffffff'}`,
      `color:${dark ? '#ffffff' : '#1c1c1e'}`,
      'font:500 14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
      'box-shadow:0 6px 24px rgba(0,0,0,0.25)',
      'opacity:0',
      'transition:opacity .2s ease',
      'pointer-events:none',
    ].join(';');
    document.body.appendChild(el);
    requestAnimationFrame(() => {
      el.style.opacity = '1';
    });
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 250);
    }, 3000);
  }

  showWalletPicker(
    wallets: WalletMeta[],
    onSelect: (wallet: WalletMeta) => void,
  ): void {
    this.accent = DEFAULT_ACCENT;
    this.mono = false;
    const useGrid = wallets.length >= 3;

    const listHtml = useGrid
      ? `<ul class="wc-wallet-grid">${wallets
          .map(
            (w) => `
        <li class="wc-wallet-grid-item" data-wallet-id="${w.walletId}">
          ${
            w.icon
              ? `<img class="wc-wallet-grid-icon" src="${w.icon}" alt="${w.name}" />`
              : `<div class="wc-wallet-grid-icon-placeholder">${w.name.charAt(0)}</div>`
          }
          <span class="wc-wallet-grid-name">${w.name}</span>
        </li>`,
          )
          .join('')}</ul>`
      : `<ul class="wc-wallet-list">${wallets
          .map(
            (w) => `
        <li class="wc-wallet-item" data-wallet-id="${w.walletId}">
          ${
            w.icon
              ? `<img class="wc-wallet-list-icon" src="${w.icon}" alt="${w.name}" />`
              : `<div class="wc-wallet-list-icon-placeholder">${w.name.charAt(0)}</div>`
          }
          <div class="wc-wallet-item-info">
            <div class="wc-wallet-item-name">${w.name}</div>
            ${w.description ? `<div class="wc-wallet-item-desc">${w.description}</div>` : ''}
          </div>
          <span class="wc-wallet-item-arrow">&#8250;</span>
        </li>`,
          )
          .join('')}</ul>`;

    const html = `
      <div class="wc-header">
        <h3 class="wc-title">Connect Wallet</h3>
        <button class="wc-close" data-action="close">&#10005;</button>
      </div>
      <div class="wc-mascot">
        <img src="${MASCOT_URL}" alt="mascot" />
      </div>
      <p class="wc-subtitle">Choose a wallet to get started</p>
      <div class="wc-body">${listHtml}</div>
      <div class="wc-footer">Powered by TonCorp</div>
    `;

    this.render(html);

    wallets.forEach((w) => {
      this.overlay
        ?.querySelector(`[data-wallet-id="${w.walletId}"]`)
        ?.addEventListener('click', () => onSelect(w));
    });
  }

  showConnect(wallet: WalletMeta, deeplink: string): void {
    this.accent = wallet.accent || DEFAULT_ACCENT;
    this.mono = !!wallet.mono;
    const qrUrl = generateQRDataUrl(deeplink);

    const deeplinkBtnHtml = `
      <a class="wc-deeplink-btn" href="${deeplink}" target="_blank">
        ${TELEGRAM_SVG}
        <span>Open in Telegram</span>
      </a>`;
    const qrBlock = `
      <div class="wc-qr-container">
        <div class="wc-qr-wrap">
          <img src="${qrUrl}" width="200" height="200" alt="QR" />
        </div>
        <div class="wc-qr-hint">Scan with your phone camera or wallet app</div>
      </div>`;
    const statusBlock = `
      <div class="wc-status">
        <div class="wc-spinner"></div>
        <span>Waiting for confirmation...</span>
      </div>`;

    // Mono wallets (TonPass) show QR + button together; others keep Mobile/QR tabs.
    const bodyInner = this.mono
      ? `${qrBlock}<div class="wc-or"><span>OR</span></div>${deeplinkBtnHtml}${statusBlock}`
      : `
        <div class="wc-tabs">
          <button class="wc-tab active" data-tab="mobile"><span>Mobile</span></button>
          <button class="wc-tab" data-tab="qr"><span>QR Code</span></button>
        </div>
        <div data-panel="mobile">${deeplinkBtnHtml}</div>
        <div data-panel="qr" style="display:none">${qrBlock}</div>
        ${statusBlock}`;

    const html = `
      <div class="wc-header">
        <div class="wc-header-left">
          ${wallet.icon ? `<img class="wc-header-icon" src="${wallet.icon}" />` : ''}
          <h3 class="wc-title">${wallet.name}</h3>
        </div>
        <button class="wc-close" data-action="close">&#10005;</button>
      </div>
      <p class="wc-subtitle">Approve the connection in your wallet app</p>
      <div class="wc-body">${bodyInner}</div>
      <div class="wc-footer">Powered by ${wallet.company || wallet.name}</div>
    `;

    this.render(html);

    // Trong Telegram: mở onewallet bằng openTelegramLink (mở Mini App đúng chuẩn).
    // dex có thể bị đóng -> site_tma_url + resume lo phần quay lại + hiện kết quả.
    // Ngoài Telegram: để thẻ <a target="_blank"> xử lý bình thường.
    const deeplinkBtn = this.overlay?.querySelector('.wc-deeplink-btn');
    deeplinkBtn?.addEventListener('click', (e) => {
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.openTelegramLink) {
        e.preventDefault();
        tg.openTelegramLink(deeplink);
      }
    });

    const tabs = this.overlay?.querySelectorAll('.wc-tab');
    const panels = this.overlay?.querySelectorAll('[data-panel]');
    tabs?.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = (tab as HTMLElement).dataset.tab;
        tabs.forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');
        panels?.forEach((p) => {
          (p as HTMLElement).style.display =
            (p as HTMLElement).dataset.panel === target ? 'block' : 'none';
        });
      });
    });
  }

  showSuccess(
    wallet: WalletMeta,
    address: string | null,
    user: { username?: string; avatar?: string } | null,
    onDone: () => void,
  ): void {
    this.accent = wallet.accent || DEFAULT_ACCENT;
    this.mono = !!wallet.mono;
    const checkSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

    const displayName = user?.username || 'User';
    const initials = displayName.slice(0, 2).toUpperCase();

    const avatarHtml = user?.avatar
      ? `<img class="wc-success-avatar" src="${user.avatar}" alt="${displayName}" />`
      : `<div class="wc-success-avatar-placeholder">${initials}</div>`;

    // Mono wallets (TonPass) show no user card — no avatar, name or wallet address.
    const userBlock = this.mono
      ? ''
      : `
          <div class="wc-success-user">
            ${avatarHtml}
            <div class="wc-success-user-info">
              <div class="wc-success-user-name">${displayName}</div>
              ${address ? `<div class="wc-success-user-addr" title="${address}">${address}</div>` : ''}
            </div>
          </div>`;

    const html = `
      <div class="wc-header">
        <h3 class="wc-title">Connected</h3>
        <button class="wc-close" data-action="close">&#10005;</button>
      </div>
      <div class="wc-body">
        <div class="wc-success">
          <div class="wc-success-check">${checkSvg}</div>
          <div class="wc-success-title">Connection Successful</div>
          ${userBlock}
          <button class="wc-success-done" data-action="done"><span>Done</span></button>
        </div>
      </div>
      <div class="wc-footer">Powered by ${wallet.company || wallet.name}</div>
    `;

    this.render(html);

    this.overlay
      ?.querySelector('[data-action="done"]')
      ?.addEventListener('click', onDone);
  }

  showFailed(
    walletName: string,
    title: string,
    message: string,
    onDone: () => void,
    accent?: string,
    mono = false,
  ): void {
    this.accent = accent || DEFAULT_ACCENT;
    this.mono = mono;
    const crossSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`;

    const html = `
      <div class="wc-header">
        <h3 class="wc-title">${title}</h3>
        <button class="wc-close" data-action="close">&#10005;</button>
      </div>
      <div class="wc-body">
        <div class="wc-success">
          <div class="wc-success-check" style="color:#e5484d;background:color-mix(in srgb,#e5484d 14%,transparent)">${crossSvg}</div>
          <div class="wc-success-title">${title}</div>
          <div class="wc-success-user-info" style="text-align:center">
            <div class="wc-success-user-name">${message}</div>
          </div>
          <button class="wc-success-done" data-action="done"><span>Close</span></button>
        </div>
      </div>
      <div class="wc-footer">Powered by ${walletName}</div>
    `;

    this.render(html);

    this.overlay
      ?.querySelector('[data-action="done"]')
      ?.addEventListener('click', onDone);
  }

  updateStatus(text: string, showSpinner = true): void {
    const el = this.overlay?.querySelector('.wc-status');
    if (el) {
      el.innerHTML = `
        ${showSpinner ? '<div class="wc-spinner"></div>' : ''}
        <span>${text}</span>
      `;
    }
  }

  private render(innerHtml: string): void {
    this.destroy();

    const el = document.createElement('div');
    el.id = MODAL_ID;
    el.innerHTML = `
      <style>${getStyles(this.theme, this.accent, this.mono)}</style>
      <div class="wc-overlay">
        <div class="wc-modal">${innerHtml}</div>
      </div>
    `;

    document.body.appendChild(el);
    this.overlay = el;

    this.overlay
      .querySelector('[data-action="close"]')
      ?.addEventListener('click', () => { this.onClose(); this.destroy(); });

    this.overlay
      .querySelector('.wc-overlay')
      ?.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).classList.contains('wc-overlay')) {
          this.onClose();
          this.destroy();
        }
      });
  }

  destroy(): void {
    document.getElementById(MODAL_ID)?.remove();
    this.overlay = null;
  }
}
